/**
 * Where a message is about, in the text the user typed.
 *
 * The compiler's messages are strings — "unknown relation 'foo'" — which is all
 * a list under the editor ever needed. An editor that underlines the mistake
 * needs the other half: *which characters* it's about. That's what a
 * {@link Span} is, and a {@link Diagnostic} is a message with one.
 *
 * Spans don't live on the AST. The parser records them in a side table keyed by
 * node identity ({@link Spans}), so `parseRule(ruleToString(r))` still returns
 * exactly `r` — the AST stays the plain object model the interpreter grounds and
 * the tests compare, and position tracking is something layered over it rather
 * than mixed into it.
 */

/** A range of source text: 1-based lines and columns, `end` exclusive. */
export interface Span {
  startLine: number;
  startColumn: number;
  endLine: number;
  endColumn: number;
}

/**
 * Node identity → the text it was parsed from. Keys are AST nodes ({@link
 * import('./ruleAst').Expr}, {@link import('./ruleAst').Quantifier}), which are
 * freshly allocated by the parser and so unique per occurrence.
 */
export type Spans = Map<unknown, Span>;

/** One problem with a rule: what's wrong, and where. */
export interface Diagnostic {
  message: string;
  /**
   * The AST node the message is about. Validation works on the AST and doesn't
   * know about text, so it reports the node and leaves the lookup to whoever
   * holds the parse's {@link Spans}.
   */
  node?: unknown;
  /** The text the message is about, once the node has been looked up. */
  span?: Span;
}

/** Resolve each diagnostic's node to its source span, where the parser recorded one. */
export function locate(diagnostics: Diagnostic[], spans: Spans, fallback?: Span): Diagnostic[] {
  return diagnostics.map((d) => {
    const span = (d.node !== undefined ? spans.get(d.node) : undefined) ?? fallback;
    return span ? { ...d, span } : d;
  });
}

/** The messages of a list of diagnostics — the form the tree and the tabs use. */
export const messagesOf = (diagnostics: Diagnostic[]): string[] => diagnostics.map((d) => d.message);
